import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import companiesService, { Company } from "../../features/companies/companiesService";
import { isAdmin } from "../../utils/permissions";
import { setSelectedClient } from "../../features/settings/settingsSlice";
import { Button } from "react-bootstrap";
import { FaPlus } from "react-icons/fa";
import { Icon } from "../../components/common/Icon";

const WhatsappChatbotsPage: React.FC = () => {
  const dummy = [
    { id: 1, name: "Support Bot", phone: "+1 (555) 111-2233", status: "active" },
    { id: 2, name: "Sales Assistant", phone: "+1 (555) 333-4455", status: "paused" },
    { id: 3, name: "Onboarding Bot", phone: "+1 (555) 666-7788", status: "draft" },
  ];

  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((s: RootState) => s.auth);
  const [singleCompanies, setSingleCompanies] = useState<Company[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | "">("");
  useEffect(() => {
    const load = async () => {
      if (!isAdmin(user)) return;
      const { companies } = await companiesService.getCompanies({ company_type: 'single', limit: 200 });
      setSingleCompanies(companies || []);
    };
    load();
  }, [user]);

  return (
    <div className="whatsapp-chatbots-page">
      <div className="page-header">
        <div className="header-content">
          <div className="header-info">
            <div className="header-icon">
              <i className="bi bi-whatsapp" />
            </div>
            <div className="header-text">
              <h1 className="page-title">WhatsApp Chatbots</h1>
              <p className="page-subtitle">Automate support and sales on WhatsApp with smart assistants</p>
            </div>
          </div>
        </div>
        <div className="header-controls">
          {isAdmin(user) && (
            <div className="company-selector">
              <select
                className="form-select"
                value={selectedCompanyId}
                onChange={(e) => {
                  const val = e.target.value ? Number(e.target.value) : "";
                  setSelectedCompanyId(val);
                  (dispatch as any)(setSelectedClient(typeof val === 'number' ? val : undefined));
                }}
              >
                <option value="">Select company</option>
                {singleCompanies.map((c) => (
                  <option key={c.id} value={c.id}>{c.company_name}</option>
                ))}
              </select>
            </div>
          )}
          {isAdmin(user) && (
            <Button className="btn-standard btn-primary">
              <Icon icon={FaPlus} />
              Add New WhatsApp Bot
            </Button>
          )}
        </div>
      </div>

      <div className="bots-grid">
        {dummy.map((b) => (
          <div key={b.id} className="bot-card">
            <div className="bot-header">
              <div className="bot-icon"><i className="bi bi-robot" /></div>
              <div className="bot-meta">
                <div className="bot-title">{b.name}</div>
                <div className="bot-sub">{b.phone}</div>
              </div>
              <div className={`status ${b.status}`}>{b.status}</div>
            </div>
            <div className="bot-actions">
              <button className="btn btn-sm btn-outline-primary">View Metrics</button>
              <button className="btn btn-sm btn-outline-secondary">Configure</button>
            </div>
          </div>
        ))}
      </div>

      <style>{`
.whatsapp-chatbots-page { 
  --bg-primary: #ffffff;
  --bg-secondary: #f8fafc;
  --bg-tertiary: #f1f5f9;
  --text-primary: #0f172a;
  --text-secondary: #475569;
  --text-muted: #64748b;
  --border-color: #e2e8f0;
  --shadow-light: 0 1px 3px rgba(0, 0, 0, 0.1);
  --shadow-medium: 0 4px 6px rgba(0, 0, 0, 0.07);
  --shadow-hover: 0 8px 25px rgba(0, 0, 0, 0.15);
  --accent-primary: #22c55e;
  --accent-secondary: #16a34a;
  --success-color: #10b981;
  --warning-color: #f59e0b;
  --danger-color: #ef4444;
  --info-color: #06b6d4;
  padding: 24px;
  background: var(--bg-secondary);
  min-height: 100vh;
}

/* Dark Theme */
[data-bs-theme="dark"] .whatsapp-chatbots-page {
  --bg-primary: #1e293b;
  --bg-secondary: #0f172a;
  --bg-tertiary: #334155;
  --text-primary: #f8fafc;
  --text-secondary: #cbd5e1;
  --text-muted: #94a3b8;
  --border-color: #475569;
  --shadow-light: 0 1px 3px rgba(0, 0, 0, 0.3);
  --shadow-medium: 0 4px 6px rgba(0, 0, 0, 0.2);
  --shadow-hover: 0 8px 25px rgba(0, 0, 0, 0.4);
}

/* Header Styles */
.whatsapp-chatbots-page .page-header {
  margin-bottom: 24px;
  padding: 20px 24px;
  background: var(--bg-primary);
  border-radius: 16px;
  box-shadow: var(--shadow-light);
  border: 1px solid var(--border-color);
  transition: all 0.3s ease;
}

.whatsapp-chatbots-page .header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 20px;
  margin-bottom: 16px;
}

.whatsapp-chatbots-page .header-info {
  display: flex;
  align-items: center;
  gap: 16px;
}

.whatsapp-chatbots-page .header-icon {
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, var(--accent-primary) 0%, var(--accent-secondary) 100%);
  border-radius: 12px;
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.25);
  transition: all 0.3s ease;
}

.whatsapp-chatbots-page .header-icon:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(34, 197, 94, 0.35);
}

.whatsapp-chatbots-page .header-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.whatsapp-chatbots-page .page-title {
  font-size: 24px;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
  line-height: 1.2;
}

.whatsapp-chatbots-page .page-subtitle {
  font-size: 14px;
  color: var(--text-muted);
  margin: 0;
  font-weight: 500;
}

.whatsapp-chatbots-page .header-actions {
  display: flex;
  gap: 12px;
}

.whatsapp-chatbots-page .header-controls {
  display: flex;
  gap: 12px;
  align-items: center;
  flex-wrap: wrap;
}

.whatsapp-chatbots-page .company-selector {
  min-width: 220px;
}

/* Button Styles */
.whatsapp-chatbots-page .btn-standard {
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.whatsapp-chatbots-page .btn-primary {
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary));
  color: white;
  box-shadow: 0 4px 12px rgba(34, 197, 94, 0.25);
}

.whatsapp-chatbots-page .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(34, 197, 94, 0.35);
  color: white;
}

/* Grid and card styles */
.whatsapp-chatbots-page .bots-grid { 
  display: grid; 
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); 
  gap: 14px; 
  margin-top: 12px; 
}

.whatsapp-chatbots-page .bot-card { 
  background: var(--bg-primary); 
  border: 1px solid var(--border-color); 
  border-radius: 16px; 
  padding: 16px; 
  transition: transform .2s ease, box-shadow .2s ease, border-color .2s ease; 
}

.whatsapp-chatbots-page .bot-card:hover { 
  transform: translateY(-3px); 
  box-shadow: 0 14px 30px rgba(15,23,42,.15); 
  border-color: rgba(34, 197, 94, 0.5); 
}

.whatsapp-chatbots-page .bot-header { 
  display: flex; 
  align-items: center; 
  gap: 12px; 
}

.whatsapp-chatbots-page .bot-icon { 
  width: 36px; 
  height: 36px; 
  border-radius: 10px; 
  background: linear-gradient(135deg, var(--accent-primary), var(--accent-secondary)); 
  color: #fff; 
  display: flex; 
  align-items: center; 
  justify-content: center; 
}

.whatsapp-chatbots-page .bot-meta { 
  flex: 1; 
  min-width: 0; 
}

.whatsapp-chatbots-page .bot-title { 
  font-weight: 800; 
  color: var(--text-primary); 
}

.whatsapp-chatbots-page .bot-sub { 
  color: var(--text-secondary); 
  font-size: 13px; 
  margin-top: 2px; 
}

.whatsapp-chatbots-page .status { 
  display: inline-block; 
  margin-top: 10px; 
  padding: 4px 10px; 
  border-radius: 999px; 
  border: 1px solid var(--border-color); 
  font-size: 12px; 
  text-transform: capitalize; 
}

.whatsapp-chatbots-page .status.active { 
  color: var(--success-color); 
  border-color: rgba(16,185,129,.4); 
  background: rgba(16,185,129,.08); 
}

.whatsapp-chatbots-page .status.paused { 
  color: var(--warning-color); 
  border-color: rgba(245,158,11,.4); 
  background: rgba(245,158,11,.08); 
}

.whatsapp-chatbots-page .status.draft { 
  color: var(--text-muted); 
  border-color: rgba(100,116,139,.4); 
  background: rgba(100,116,139,.08); 
}

.whatsapp-chatbots-page .bot-actions { 
  display: flex; 
  gap: 8px; 
  margin-top: 12px; 
}
      `}</style>
    </div>
  );
};

export default WhatsappChatbotsPage;


